import { useCallback, useEffect, useState } from 'react';
import { Badge, Empty, Grid, Note, Panel, Spinner, Stat, TableWrap } from '@wyocspay/ui';
import { ApiError, api, type Dac7MissingField, type Dac7SellerRow } from './api.js';
import { money } from './format.js';

/**
 * DAC7 - отчитане към ЕС.
 *
 * Екранът отговаря на три въпроса и ги държи разделени, защото смесването им е
 * начинът, по който се подава грешна декларация:
 *
 *   - кои търговци изобщо попадат в обхвата;
 *   - какво липсва по всеки от тях, и дали липсата пречи;
 *   - какво би съдържала декларацията, ако бъде подадена днес.
 *
 * Нищо тук не се изпраща никъде само по себе си. Дори когато файлът се генерира,
 * той се сваля - платформата не е регистрирана в нито една държава членка и не
 * подава нищо автоматично.
 *
 * Дали задължението изобщо се отнася за WyoCSPay не е решено. Изключението за
 * "обработка на плащания" в Приложение V, Раздел I(A)(1) може да покрива тази
 * платформа, но същият параграф казва, че понятието включва и "всяка договореност
 * за събиране и плащане на Възнаграждение". Аргументите и от двете страни са в
 * compliance/dac7-reporting.md и се нуждаят от професионално мнение.
 */

const SEVERITY_TONE: Record<Dac7MissingField['severity'], 'fail' | 'warn' | 'neutral'> = {
  required: 'fail',
  conditional: 'warn',
  where_available: 'neutral',
};

const SEVERITY_LABEL: Record<Dac7MissingField['severity'], string> = {
  required: 'ЗАДЪЛЖИТЕЛНО',
  conditional: 'ПРИ УСЛОВИЕ',
  where_available: 'АКО Е НАЛИЧНО',
};

/**
 * Основанията за изключване, преведени.
 *
 * Кодът остава на английски навсякъде другаде - в одитния дневник, в API и в
 * самата директива - и точно него се цитира при въпрос от данъчна администрация.
 */
const EXCLUSION_LABEL: Record<string, string> = {
  GOVERNMENTAL_ENTITY: 'Държавен субект - Раздел I(B)(4)(a)',
  PUBLICLY_TRADED_ENTITY: 'Дружество на регулиран пазар - Раздел I(B)(4)(b)',
  PROPERTY_LISTING_VOLUME: 'Над 2 000 наема по един обект - Раздел I(B)(4)(c)',
  GOODS_DE_MINIMIS: 'Под прага за продажба на стоки - Раздел I(B)(4)(d)',
};

const DUE_DILIGENCE_LABEL: Record<string, string> = {
  NOT_STARTED: 'НЕ Е ЗАПОЧНАТА',
  REQUESTED: 'ПОИСКАНА',
  SUPPLIED: 'ПОДАДЕНА',
  VERIFIED: 'ПРОВЕРЕНА',
  REFUSED: 'ОТКАЗАНА',
};

const ACTIVITY_LABEL: Record<string, string> = {
  IMMOVABLE_PROPERTY_RENTAL: 'Отдаване на недвижим имот',
  PERSONAL_SERVICE: 'Лична услуга',
  SALE_OF_GOODS: 'Продажба на стоки',
  TRANSPORT_RENTAL: 'Отдаване на транспортно средство',
};

function years(): number[] {
  const current = new Date().getUTCFullYear();
  // 2023 е първият отчетен период по член 8ac(3), така че по-рано няма смисъл.
  const list: number[] = [];
  for (let y = current - 1; y >= 2023; y -= 1) list.push(y);
  return list.length > 0 ? list : [current - 1];
}

export function Dac7View() {
  const [year, setYear] = useState(new Date().getUTCFullYear() - 1);
  const [data, setData] = useState<Awaited<ReturnType<typeof api.dac7>> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [xmlCheck, setXmlCheck] = useState<Awaited<ReturnType<typeof api.dac7XmlCheck>> | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setData(await api.dac7(year));
      setXmlCheck(await api.dac7XmlCheck(year));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  }, [year]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Файловете носят дати на раждане и данъчни номера, така че се свалят с
   * токена в заглавието вместо през обикновен линк. Един линк с ключ в адреса
   * би останал в историята на браузъра.
   */
  async function download(format: 'csv' | 'xml'): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const blob = await api.dac7Download(year, format);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `dac7-${year}.${format}`;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  if (error && !data) return <Note tone="danger">{error}</Note>;
  if (!data) return <Spinner label="Зареждане…" />;

  const reportable = data.sellers.filter((s) => s.reportable && s.undetermined.length === 0);
  const undecided = data.sellers.filter((s) => s.undetermined.length > 0);
  const excluded = data.sellers.filter((s) => s.excluded);

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {error ? <Note tone="danger">{error}</Note> : null}

      <Note title="Още не е решено дали това задължение изобщо възниква">
        Приложение V, Раздел I(A)(1) от Директива (ЕС) 2021/514 изключва софтуер, който
        <em> изключително </em> позволява „обработка на плащания във връзка с Съответна дейност“ и
        не се намесва по друг начин. Същият параграф обаче казва, че „Платформа“ включва и „всяка
        договореност за събиране и плащане на Възнаграждение“. WyoCSPay е и двете едновременно, а
        текстът не решава спора. Всичко на този екран е подготовка, не отговор -
        вижте compliance/dac7-reporting.md.
      </Note>

      <Panel
        title="Отчетен период"
        actions={
          <select
            className="wc-input"
            style={{ width: 'auto' }}
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
          >
            {years().map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        }
      >
        <Grid cols={4}>
          <Stat label="Разгледани" value={data.counts.surveyed} accent="var(--wc-grey)" />
          <Stat
            label="За отчитане"
            value={reportable.length}
            accent="var(--wc-blue)"
            hint="Активни продавачи, които не са изключени и са местни лица в държава членка"
          />
          <Stat
            label="Чакат решение"
            value={undecided.length}
            accent="var(--wc-orange)"
            hint={undecided.length > 0 ? 'Не могат да се класифицират автоматично' : undefined}
          />
          <Stat label="Изключени" value={excluded.length} accent="var(--wc-green)" />
        </Grid>
        <Note>
          Срокът за подаване е 31 януари {year + 1} г. - Раздел III(A)(1). Дължимата грижа по
          Раздел II(F)(1) е трябвало да приключи до 31 декември {year} г.
        </Note>
      </Panel>

      <Panel title="Кой подава декларацията">
        {data.operator.missing.length === 0 ? (
          <Note tone="ok" title="Данните на оператора са попълнени">
            {data.operator.name} · ДИН {data.operator.tin} · регистрация в{' '}
            {data.operator.memberStateOfRegistration} · номер{' '}
            {data.operator.individualIdentificationNumber}
          </Note>
        ) : (
          <>
            <Note tone="danger" title="Липсват данни за самия оператор">
              Без тях декларация не може да се подаде никъде. Попълват се от Настройки на
              платформата - те са подписани команди, като всяка друга промяна на настройка.
            </Note>
            <ul style={{ margin: '10px 0 0', paddingLeft: 18, display: 'grid', gap: 6, fontSize: 13.5 }}>
              {data.operator.missing.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </>
        )}
        <Note tone="warn" title="Регистрация в една държава членка">
          Оператор извън Съюза се регистрира при компетентния орган на <strong>една</strong> държава
          членка по свой избор - член 8ac(4) и Приложение V, Раздел IV(F)(1). Тя издава индивидуален
          идентификационен номер и уведомява останалите. Регистрацията е отделно действие, което не
          се извършва от тази платформа.
        </Note>
      </Panel>

      <Panel
        title="Търговци в обхвата"
        actions={
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              className="wc-btn wc-btn-sm"
              disabled={busy}
              onClick={() => void download('csv')}
            >
              Свали CSV
            </button>
            <button
              type="button"
              className="wc-btn wc-btn-sm"
              disabled={busy}
              onClick={() => void download('xml')}
            >
              Свали XML
            </button>
          </div>
        }
      >
        {data.sellers.length === 0 ? (
          <Empty>Никой търговец не показва връзка с ЕС за {year} г.</Empty>
        ) : (
          <TableWrap>
            <table className="wc-table">
              <thead>
                <tr>
                  <th>Търговец</th>
                  <th>Държави членки</th>
                  <th>Дължима грижа</th>
                  <th>Решение</th>
                  <th style={{ textAlign: 'right' }}>Възнаграждение</th>
                  <th style={{ textAlign: 'right' }}>Дейности</th>
                  <th>Липсва</th>
                </tr>
              </thead>
              <tbody>
                {data.sellers.map((seller) => (
                  <SellerRow
                    key={seller.merchant_id}
                    seller={seller}
                    expanded={open === seller.merchant_id}
                    onToggle={() =>
                      setOpen(open === seller.merchant_id ? null : seller.merchant_id)
                    }
                  />
                ))}
              </tbody>
            </table>
          </TableWrap>
        )}
      </Panel>

      {xmlCheck ? (
        <Panel title="Какво казва генераторът на XML за себе си">
          <Grid cols={2}>
            <Stat
              label="Записи в съобщението"
              value={xmlCheck.records}
              accent={xmlCheck.records > 0 ? 'var(--wc-blue)' : 'var(--wc-grey)'}
            />
            <Stat
              label="Спиращи проблеми"
              value={xmlCheck.blockers.length}
              accent={xmlCheck.blockers.length > 0 ? 'var(--wc-red)' : 'var(--wc-green)'}
            />
          </Grid>

          {xmlCheck.blockers.length > 0 ? (
            <Note tone="danger" title="Файлът не бива да се подава в този вид">
              <ul style={{ margin: '6px 0 0', paddingLeft: 18, display: 'grid', gap: 5 }}>
                {xmlCheck.blockers.map((b) => (
                  <li key={b}>{b}</li>
                ))}
              </ul>
            </Note>
          ) : null}

          {xmlCheck.caveats.length > 0 ? (
            <Note tone="warn" title="Какво генераторът е допуснал или е оставил настрана">
              <ul style={{ margin: '6px 0 0', paddingLeft: 18, display: 'grid', gap: 5 }}>
                {xmlCheck.caveats.map((c) => (
                  <li key={c}>{c}</li>
                ))}
              </ul>
            </Note>
          ) : null}

          <Note>
            Схемата е тази, публикувана като Приложение XIV към Регламент за изпълнение (ЕС)
            2022/1467, същата и в OECD DPI XML Schema v1.0. Файлът се проверява срещу нея, но
            отделни държави членки налагат по-строги национални схеми - Франция например използва
            TaxesQ1..TaxesQ4 вместо TaxQ1..TaxQ4. Проверявайте срещу схемата на приемащата държава
            преди подаване.
          </Note>
        </Panel>
      ) : null}
    </div>
  );
}

function SellerRow({
  seller,
  expanded,
  onToggle,
}: {
  seller: Dac7SellerRow;
  expanded: boolean;
  onToggle: () => void;
}) {
  const blocking = seller.missing.filter((m) => m.severity === 'required');
  const decision =
    seller.undetermined.length > 0 ? (
      <Badge tone="warn">ЧАКА РЕШЕНИЕ</Badge>
    ) : seller.excluded ? (
      <Badge tone="neutral" icon={false}>
        ИЗКЛЮЧЕН
      </Badge>
    ) : seller.reportable ? (
      <Badge tone="info" icon={false}>
        ЗА ОТЧИТАНЕ
      </Badge>
    ) : (
      <Badge tone="ok">НЕ СЕ ОТЧИТА</Badge>
    );

  return (
    <>
      <tr style={{ cursor: 'pointer' }} onClick={onToggle}>
        <td>
          <strong>{seller.merchant_name}</strong>
          {seller.enforcement_due ? (
            <div style={{ fontSize: 12, color: 'var(--wc-red)' }}>
              Раздел IV(A)(2): две напомняния и над 60 дни - профилът трябва да се затвори или
              плащанията да се задържат
            </div>
          ) : null}
        </td>
        <td>{seller.resident_member_states.join(', ') || '-'}</td>
        <td>
          <Badge
            tone={
              seller.due_diligence_status === 'VERIFIED' ? 'ok'
              : seller.due_diligence_status === 'SUPPLIED' ? 'info'
              : seller.due_diligence_status === 'REFUSED' ? 'fail'
              : 'pending'
            }
            icon={false}
          >
            {DUE_DILIGENCE_LABEL[seller.due_diligence_status] ?? seller.due_diligence_status}
          </Badge>
        </td>
        <td>{decision}</td>
        <td style={{ textAlign: 'right' }} className="wc-num">
          {/*
            Всяка валута на свой ред. Сборът на евро и долари не е сума, която
            съществува - вижте services/money.ts.
          */}
          {seller.totals.length === 0
            ? '-'
            : seller.totals.map((t) => (
                <div key={t.currency}>{money(t.consideration, t.currency)}</div>
              ))}
        </td>
        <td style={{ textAlign: 'right' }} className="wc-num">
          {seller.activity_count}
        </td>
        <td>
          {blocking.length > 0 ? (
            <Badge tone="fail" icon={false}>
              {blocking.length}
            </Badge>
          ) : (
            <Badge tone="ok">пълно</Badge>
          )}
        </td>
      </tr>

      {expanded ? (
        <tr>
          <td colSpan={7} style={{ background: 'var(--wc-surface-2, #fafbfc)' }}>
            <div style={{ display: 'grid', gap: 12, padding: '10px 4px' }}>
              <div>
                <strong style={{ fontSize: 13 }}>Съответни дейности</strong>
                <p style={{ margin: '4px 0 0', fontSize: 13 }}>
                  {seller.relevant_activities.length === 0
                    ? 'Не са декларирани. Без тях прагът по Раздел I(B)(4)(d) не може да се приложи - той обхваща само продажба на стоки.'
                    : seller.relevant_activities.map((k) => ACTIVITY_LABEL[k] ?? k).join(' · ')}
                </p>
              </div>

              <div>
                <strong style={{ fontSize: 13 }}>Как е стигнато до това решение</strong>
                <ul style={{ margin: '4px 0 0', paddingLeft: 18, fontSize: 13, display: 'grid', gap: 4 }}>
                  {seller.reasons.map((reason, i) => (
                    <li key={i}>{reason}</li>
                  ))}
                </ul>
              </div>

              {seller.exclusion ? (
                <Note tone="ok" title="Основание за изключване">
                  {EXCLUSION_LABEL[seller.exclusion] ?? seller.exclusion}
                </Note>
              ) : null}

              {seller.undetermined.length > 0 ? (
                <Note tone="warn" title="Какво не може да се реши автоматично">
                  <ul style={{ margin: '6px 0 0', paddingLeft: 18, display: 'grid', gap: 5 }}>
                    {seller.undetermined.map((u, i) => (
                      <li key={i}>{u}</li>
                    ))}
                  </ul>
                  <p style={{ margin: '8px 0 0' }}>
                    До решаването им търговецът се води за отчитане. Пропуснато отчитане е санкция
                    по член 25а; отчитане на някой, който не е бил длъжен, е въпрос с човешки
                    отговор.
                  </p>
                </Note>
              ) : null}

              {seller.property_member_states.length > 0 ? (
                <Note>
                  Отдава недвижим имот в: {seller.property_member_states.join(', ')}. Това само по
                  себе си прави продавача отчетен, независимо къде е местно лице -
                  Раздел I(B)(3).
                </Note>
              ) : null}

              {seller.missing.length > 0 ? (
                <div>
                  <strong style={{ fontSize: 13 }}>Липсващи данни</strong>
                  <TableWrap>
                    <table className="wc-table" style={{ marginTop: 4 }}>
                      <thead>
                        <tr>
                          <th>Данна</th>
                          <th>Тежест</th>
                          <th>Основание</th>
                          <th>Бележка</th>
                        </tr>
                      </thead>
                      <tbody>
                        {seller.missing.map((field, i) => (
                          <tr key={`${field.field}:${i}`}>
                            <td>{field.label}</td>
                            <td>
                              <Badge tone={SEVERITY_TONE[field.severity]} icon={false}>
                                {SEVERITY_LABEL[field.severity]}
                              </Badge>
                            </td>
                            <td style={{ fontSize: 12.5, color: 'var(--wc-faint)' }}>
                              {field.citation}
                            </td>
                            <td style={{ fontSize: 12.5, color: 'var(--wc-muted)' }}>
                              {field.note ?? ''}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </TableWrap>
                </div>
              ) : null}

              {seller.totals.length > 1 ? (
                <Note tone="warn" title="Няколко валути">
                  Възнаграждението се отчита във валутата, в която е платено - Раздел III(A)(6).
                  Прагът от 2 000 EUR обаче е в евро и директивата не дава курс за преизчисляване,
                  затова той не се прилага автоматично тук.
                </Note>
              ) : null}
            </div>
          </td>
        </tr>
      ) : null}
    </>
  );
}
